/* eslint-disable import/prefer-default-export */
import { useStateMachine } from "little-state-machine";
import moment from "moment";
import { QueryFunctionContext, UseQueryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { getShipments } from "api/services/shipment";
import { updateLastShipmentRequestTime } from "store/store";
import { getObjectByKeys } from "helpers/commonHelpers";
import { shipmentQueryKeys } from "./shipmentQueryKeys";
import { ShipmentAdditionalColumns } from "../../../constants";

export const useGetAllShipments = (options?: UseQueryOptions) => {
  const queryClient = useQueryClient();
  const { state, actions }: any = useStateMachine({ updateLastShipmentRequestTime });
  const queryRes = useQuery({
    queryKey: shipmentQueryKeys.allShipments(),
    queryFn: getShipmentsFn,
    ...options,
  });

  function getShipmentsFn({ signal }: QueryFunctionContext) {
    const existingData: any = queryClient.getQueryData(shipmentQueryKeys.allShipments());
    const shipmentPropertiesToFetch = `${ShipmentAdditionalColumns.join(",")},pickup_zipcode,stop1_zipcode`;
    return getShipments({
      params: {
        page_size: false,
        properties: shipmentPropertiesToFetch,
        ...(state.lastShipmentRequestTime && existingData?.length && { updated_since: state.lastShipmentRequestTime }),
      },
      signal,
    })
      .then((res) => {
        let shipmentResponse = res.data;
        /**
         * if we already have existing data and lastRequestTime then backend
         * won't send all the data instead only data that has been updated since that
         * last time so we'll update it on our end as well.
         */
        if (state.lastShipmentRequestTime && existingData?.length) {
          shipmentResponse = updateShipmentData(existingData, shipmentResponse);
        }
        if (res.data?.length) {
          actions.updateLastShipmentRequestTime({ lastShipmentRequestTime: Date.now() });
        }
        return Promise.resolve(shipmentResponse);
      })
      .catch((err) => Promise.reject(err));
  }

  const updateShipmentData = (existingData: any, currentData: any) => {
    const existingDataHashMap = getObjectByKeys(existingData, "shipment_reference");
    const currentDataHashMap = getObjectByKeys(currentData, "shipment_reference");
    const newData = {
      ...existingDataHashMap,
      ...currentDataHashMap,
    };
    const newList = Object.values(newData);
    return newList?.sort((a: any, b: any) => (moment(b.created).isBefore(moment(a.created)) ? -1 : 1));
  };

  return queryRes;
};
